import { cn } from '@ezhire/ui';

/**
 * Round-square avatar for agents. If `url` is null, renders a color-chip
 * with up-to-2-letter initials derived from the name. Color is stable for
 * a given name (hash → HSL).
 */
export function AgentAvatar({
  name,
  url,
  size = 40,
  className,
}: {
  name: string;
  url?: string | null;
  size?: number;
  className?: string;
}) {
  const initials = getInitials(name);
  const bg = hashColor(name);

  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className={cn('rounded-lg object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-lg font-semibold text-text-inverse',
        className,
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: bg,
        fontSize: Math.max(12, size * 0.4),
      }}
      aria-label={name}
    >
      {initials}
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const second = parts[1] ?? '';
  return `${first[0] ?? ''}${second[0] ?? ''}`.toUpperCase() || '??';
}

function hashColor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  const hue = Math.abs(h) % 360;
  // Muted accent-ish palette tuned for both light + dark surfaces.
  return `hsl(${hue}, 55%, 55%)`;
}
